import { exchange, SignalError, signOff } from "../../lib/webrtc";

/**
 * Publishing a MediaStream over WHIP.
 *
 * WHIP is WebRTC-HTTP Ingestion: one POST of an SDP offer, one SDP answer back,
 * and the browser is publishing. Everything that makes streaming hard -
 * hardware encoding, congestion control, packet loss recovery, reconnection
 * after a network change - is the WebRTC stack's problem rather than ours.
 *
 * The alternative in a browser is `MediaRecorder` over a WebSocket, which means
 * owning the buffering, the backpressure and the reconnect logic, and getting
 * several seconds of latency for the trouble.
 *
 * ## The URL is a secret
 *
 * It comes from `GET /me/station/stream`, which is authenticated and behind the
 * studio flag, and anybody holding it can broadcast to the station. It is not
 * stored, not logged, and not put anywhere a listener can reach - see
 * `StreamIngest` on the backend, which is the only thing that ever hands it out.
 */

export interface Publication {
  /** `connected`, `disconnected`, `failed`, `closed`. */
  onStateChange(handler: (state: RTCPeerConnectionState) => void): void;
  /**
   * Swap what is being sent as video, without renegotiating.
   *
   * `replaceTrack` keeps the sender, its codec preferences and the SDP as they
   * are - so switching between the composited canvas and a camera mid-broadcast
   * costs a frame, not a reconnection.
   */
  replaceVideo(track: MediaStreamTrack): Promise<void>;
  stop(): Promise<void>;
}

/**
 * Publish a stream, resolving once the ingest endpoint has accepted the offer.
 *
 * Resolving on the answer rather than on `connected` is deliberate: the answer
 * is when the *server* has agreed, and ICE can legitimately take several more
 * seconds. A host whose button spins through ICE negotiation assumes it has
 * hung. The connection state is reported afterwards instead.
 */
export async function publish(
  url: string,
  stream: MediaStream,
): Promise<Publication> {
  const pc = new RTCPeerConnection({ iceServers: [] });

  let videoSender: RTCRtpSender | null = null;
  let sendingVideo = false;
  let h264Ready = false;

  for (const track of stream.getTracks()) {
    const transceiver = pc.addTransceiver(track, {
      direction: "sendonly",
      streams: [stream],
    });

    if (track.kind === "video") {
      sendingVideo = true;
      h264Ready = preferH264(transceiver);
      videoSender = transceiver.sender;
    }
  }

  let accepted;

  try {
    accepted = await exchange(url, pc);
  } catch (cause) {
    pc.close();

    /*
     * The refusal we can explain.
     *
     * An ingest that takes H.264 and an engine that cannot produce it is not a
     * network problem, and the status the endpoint returns for it reads like
     * one. Said here because this is the only place that knows both halves.
     */
    if (sendingVideo && !h264Ready) {
      throw new Error(
        "This app cannot send video: it has no usable H.264 encoder, and the " +
          "broadcast server accepts nothing else. Broadcast from the website " +
          "in Chrome, Edge or Safari instead. (Video codecs available here: " +
          videoCodecNames() +
          ")",
        { cause },
      );
    }

    if (cause instanceof SignalError && cause.status === 404) {
      /*
       * The one worth saying apart. A 404 here is the live input not existing
       * any more - rotated from another tab, or deleted in the Cloudflare
       * dashboard - and reloading re-fetches credentials, which fixes it. Every
       * other status is ours to look at rather than the host's.
       */
      throw new SignalError(
        "This broadcast link is no longer valid. Reload the page to get a new one.",
        404,
        { cause },
      );
    }

    throw cause;
  }

  await pc.setRemoteDescription({ type: "answer", sdp: accepted.answer });

  const handlers: ((state: RTCPeerConnectionState) => void)[] = [];

  pc.addEventListener("connectionstatechange", () => {
    for (const handler of handlers) handler(pc.connectionState);
  });

  return {
    onStateChange: (handler) => {
      handlers.push(handler);
      handler(pc.connectionState);
    },
    replaceVideo: async (track) => {
      await videoSender?.replaceTrack(track);
    },
    stop: async () => {
      pc.close();
      await signOff(accepted.resource);
    },
  };
}

/**
 * Ask for H.264 ahead of anything else.
 *
 * ## This is not an optimisation - without it there is no broadcast
 *
 * Chromium offers VP8 first and will happily agree on it with a peer that
 * accepts it. Cloudflare Stream does not: its WHIP endpoint takes H.264 video
 * and Opus audio, and an offer without H.264 is refused outright.
 *
 * This used to be worse and quieter. The old self-hosted ingest accepted VP8,
 * connected, reported `connected`, cut segments - and published audio only,
 * because its HLS muxer silently dropped the video track. Every signal a
 * broadcaster would look at said the stream was fine. A refusal at the
 * handshake is the better failure of the two by a wide margin.
 *
 * Preferred rather than forced: the remaining codecs stay in the list behind
 * H.264, so a browser without it still produces a valid offer and gets a clear
 * rejection rather than failing somewhere further down with a worse message.
 *
 * ## Asking is allowed to fail
 *
 * `RTCRtpSender.getCapabilities` answers for the engine in general;
 * `setCodecPreferences` validates against what this transceiver can actually
 * offer, and the two disagree on builds that advertise H.264 without being able
 * to encode it. The desktop shell is one: it listed H.264 and then threw
 * "invalid codec with name H264" from the call below, which killed the
 * broadcast before an offer existed - a worse failure than the one this
 * function is here to prevent.
 *
 * So the request is attempted and its refusal is reported rather than thrown.
 * The caller carries on with the engine's own ordering and finds out at the
 * handshake, where the error can say something useful.
 *
 * @returns whether H.264 could actually be moved to the front.
 */
/**
 * What this engine says it can send, for an error message to carry.
 *
 * A host cannot open a console in the desktop shell, so a failure that depends
 * on which codecs a build shipped with has to say which those were, or the
 * report that comes back is "it did not work".
 */
function videoCodecNames(): string {
  const codecs = RTCRtpSender.getCapabilities("video")?.codecs;

  if (!codecs || codecs.length === 0) return "none";

  // "video/H264" -> "H264". No regex: the prefix is the only slash in it.
  return [...new Set(codecs.map((c) => c.mimeType.split("/").pop() ?? c.mimeType))].join(
    ", ",
  );
}

function preferH264(transceiver: RTCRtpTransceiver): boolean {
  const codecs = RTCRtpSender.getCapabilities("video")?.codecs;

  if (!codecs || typeof transceiver.setCodecPreferences !== "function") {
    return false;
  }

  const h264 = codecs.filter((c) => c.mimeType.toLowerCase() === "video/h264");

  if (h264.length === 0) return false;

  try {
    transceiver.setCodecPreferences([
      ...h264,
      ...codecs.filter((c) => c.mimeType.toLowerCase() !== "video/h264"),
    ]);

    return true;
  } catch {
    return false;
  }
}
